import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Flex, Avatar, Icon } from '@chakra-ui/react';
import { getTheWordObj } from '@/pages/interviews/utils/transcript';
import { Play } from '@kula-ai/icons/dual-tone/Play';
import { KText } from '@/ui-library/components/text';

interface Word {
  text: string;
  start_timestamp: number;
}

interface Speech {
  speaker: string;
  words: Word[];
}

interface TranscriptData {
  transcript?: Speech[];
}

interface TranscriptProps {
  currentTime?: number;
  data: TranscriptData;
  handleTimeJump: (time: number, shouldPlay?: boolean) => void;
  playerType?: 'mini' | 'modal';
}

interface CurrentIndex {
  sentence: number;
  word: number;
}

interface TranscriptRef {
  triggerFunction: () => void;
}

export const Transcript = forwardRef<TranscriptRef, TranscriptProps>(
  ({ currentTime = 0, data, handleTimeJump, playerType = 'mini' }, ref) => {
    const { transcript } = data || {};
    const [currentIndex, setCurrentIndex] = useState<CurrentIndex>({
      sentence: 0,
      word: 0,
    });
    const [previousIndex, setPreviousIndex] = useState<CurrentIndex | null>(
      null
    );

    useEffect(() => {
      const element = document.getElementById(
        `word-${playerType}-${currentIndex?.sentence}-${currentIndex?.word}`
      );
      if (element) {
        element.style.background = '#605AFB';
        element.style.borderRadius = '3px';
        element.style.color = 'white';
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (previousIndex !== null) {
        const previousElement = document.getElementById(
          `word-${playerType}-${previousIndex?.sentence}-${previousIndex?.word}`
        );
        if (previousElement) {
          previousElement.style.background = 'inherit';
          previousElement.style.color = 'inherit';
        }
      }
      setPreviousIndex(currentIndex);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);

    useEffect(() => {
      if (transcript) {
        const obj = getTheWordObj(transcript, currentTime);
        setCurrentIndex(obj?.indexes);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime]);

    const handleCopy = () => {
      const formattedContent = transcript
        ?.map(
          (speech) =>
            `${speech?.speaker}: ${speech?.words
              .map((word) => `${word?.text} `)
              .join('')}`
        )
        .join('\n\n');

      navigator.clipboard
        .writeText(formattedContent)
        .then(() => {
          console.log('Text copied!!');
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    };

    // In this case, it allows the parent component to call the 'handleCopy' function, which copies the transcript text to the clipboard.
    // This is useful for providing a way for the parent component to trigger the copy action without needing to directly manage the state or logic of the child component.
    useImperativeHandle(ref, () => ({
      triggerFunction: handleCopy,
    }));

    return (
      //@ts-ignore
      <Flex
        flexDirection="column"
        flex={1}
        overflowY="auto"
        mt="20px"
        pb="20px"
      >
        {transcript?.map((speech, index) => (
          <Flex
            key={'speech' + index}
            mb="20px"
            pos="relative"
            alignItems="center"
            _hover={{ '> div': { visibility: 'visible', opacity: 1 } }}
          >
            <Flex>
              <Avatar
                name={speech?.speaker}
                size="xs"
                mr="12px"
                borderRadius="md"
              />
              <Flex flexDirection="column">
                <KText size="sm-semi-bold" mb="5px" color="secondary-900">
                  {speech?.speaker}
                </KText>
                <Flex wrap={'wrap'}>
                  {speech?.words.map((word, wordIndex) => (
                    <KText
                      mr="3px"
                      size="sm-regular"
                      id={`word-${playerType}-${index}-${wordIndex}`}
                      key={`word-${index}-${wordIndex}`}
                    >
                      {word?.text}
                    </KText>
                  ))}
                </Flex>
              </Flex>
            </Flex>
            <Flex
              bg="rgba(96, 90, 251, 0.16);"
              w="26px"
              h="26px"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              pos="absolute"
              right="0"
              cursor="pointer"
              visibility="hidden"
              onClick={() =>
                handleTimeJump(speech?.words?.[0]?.start_timestamp * 1000, true)
              }
            >
              <Icon as={Play} w="12px" h="12px" color="#605AFB" />
            </Flex>
          </Flex>
        ))}
      </Flex>
    );
  }
);
